import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { getSignalSnapshot, guidanceForSignal, recordCtaClick } from "../../lib/signalPathEngine";
import { useConsultation } from "../../context/ConsultationContext";
import { trackEvent } from "../../lib/track";

/**
 * SignalGuidanceCard — the quiet inline bridge between sections.
 *
 * Reads the Signal Path Engine, picks the right copy, and renders
 * one elegant invitation. Never a popup. Never an interruption.
 * Lives inline as a small black/gold glass card with a single CTA.
 *
 * Props:
 *   placement   — string identifying where this card lives ("journal-end",
 *                 "post-snapshot", "before-final-cta", etc). Used for
 *                 tracking only.
 *   forceSignal — optionally override the engine for context-specific
 *                 cards (e.g. the journal post page passes the post's
 *                 dominant tag signal so the closing speaks directly
 *                 to what was just read).
 *   hideOnLow   — when true, the card silently hides itself if the
 *                 engine reports low confidence. Good for places we
 *                 don't want any guidance unless we're sure.
 */
const SignalGuidanceCard = ({ placement = "default", forceSignal = null, hideOnLow = false, className = "" }) => {
  const { open } = useConsultation();
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    const s = getSignalSnapshot();
    setSnap(s);
    trackEvent("signal_guidance_card_shown", {
      placement,
      signal: forceSignal || s.primarySignal,
      confidence: s.confidenceLevel,
      visitor_state: s.visitorState,
    });
  }, [placement, forceSignal]);

  if (!snap) return null;
  if (hideOnLow && snap.confidenceLevel === "low" && !forceSignal) return null;

  const signal = forceSignal || snap.primarySignal;
  const guidance = guidanceForSignal(signal);

  const handleClick = (e) => {
    recordCtaClick(`guidance_${signal}`);
    trackEvent("signal_guidance_cta_clicked", {
      placement,
      signal,
      label: guidance.cta,
    });
    if (!guidance.href) {
      e.preventDefault();
      open({
        category: "Not sure yet",
        specific_interest: "Not Sure Yet",
        source_context: `Signal Guidance · ${placement}`,
        title: "Bring the question you have been circling.",
        subtitle: "Tell us where you are. We will respond within 48 hours.",
      });
    }
  };

  const Tag = guidance.href ? "a" : "button";

  return (
    <Tag
      href={guidance.href || undefined}
      onClick={handleClick}
      data-testid={`signal-guidance-card-${placement}`}
      data-signal={signal}
      data-confidence={snap.confidenceLevel}
      data-visitor-state={snap.visitorState}
      className={`signal-guidance-card group ${className}`}
    >
      <span className="signal-guidance-line" aria-hidden />
      <div className="signal-guidance-body">
        <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
          {guidance.eyebrow}
        </p>
        <p className="font-serif text-themed text-lg md:text-xl leading-relaxed">
          {guidance.line}
        </p>
        <span className="signal-guidance-cta">
          {guidance.cta} <ArrowRight size={13} />
        </span>
      </div>
    </Tag>
  );
};

export default SignalGuidanceCard;
